import axios from "axios";
import FileSaver from "file-saver";
import { notification } from "antd";

import history from "../history";
import {
	API_BASE_URL,
	APP_PREFIX_PATH,
	AUTH_PREFIX_PATH,
	CONVITE_PREFIX_PATH,
	REGISTRO_PREFIX_PATH,
	DARKSIDE_PREFIX_PATH,
	TERMOS_PREFIX_PATH,
} from "configs/AppConfig";
import { AUTH_TOKEN } from "redux/constants/Auth";

const CODIGO_BLOQUEIO_PAGAMENTO = "USUARIO_BLOQUEADO_PAGAMENTO";
const CODIGO_PENDENTE_ATIVACAO = "USUARIO_PENDENTE_ATIVACAO";
const CODIGO_TOKEN_REVOGADO = "TOKEN_REVOGADO";

let redirecionandoTokenRevogado = false;

/**
 * Verifica se a rota atual é uma rota pública (não requer autenticação)
 */
function isPublicRoute() {
	const hash = window.location.hash || "";
	const pathname = window.location.pathname || "";

	// Lista de rotas públicas
	const publicRoutes = [
		AUTH_PREFIX_PATH,
		CONVITE_PREFIX_PATH,
		REGISTRO_PREFIX_PATH,
		DARKSIDE_PREFIX_PATH,
		TERMOS_PREFIX_PATH,
	];

	// Verificar se a rota atual começa com alguma das rotas públicas
	return publicRoutes.some(
		(route) => hash.includes(route) || pathname.includes(route),
	);
}

// Operações legitimamente longas (upload de mídia, geração de PDF/ZIP,
// importações, substituição em massa de exercícios/alimentos que varre todas
// as fichas do treinador, reprocessamentos e sincronizações) ficam com o
// timeout antigo de 5 min; o resto usa o timeout curto padrão, pra request
// pendurada em rede instável virar erro tratável em segundos em vez de spinner
// eterno. (substituir não casa tabela-substituicao — 'r' vs 'c'.)
const PADROES_OPERACAO_LONGA =
	/upload|exportar|download|-pdf|-zip|importar|transcri|substituir|reprocessar|sincronizar/i;

function onFulfilledRequest(config) {
	const jwtToken = localStorage.getItem(AUTH_TOKEN);

	const isFormData =
		typeof FormData !== "undefined" && config.data instanceof FormData;
	if (
		config.timeout === TIMEOUT_PADRAO &&
		(isFormData || PADROES_OPERACAO_LONGA.test(config.url || ""))
	) {
		config.timeout = TIMEOUT_OPERACAO_LONGA;
	}

	if (jwtToken && !config.headers[TOKEN_PAYLOAD_KEY]) {
		config.headers[TOKEN_PAYLOAD_KEY] = jwtToken;
	}

	// Só redirecionar para login se:
	// 1. Não tiver token
	// 2. A requisição não for pública (sem header public-request)
	// 3. A rota atual NÃO for uma rota pública
	if (
		!jwtToken &&
		!config.headers[PUBLIC_REQUEST_KEY] &&
		!isPublicRoute()
	) {
		history.push(ENTRY_ROUTE);
	}

	return config;
}

function handleRequestError(error) {
	// Do something with request error here
	notification.error({
		message: "Error",
	});
	Promise.reject(error);
}

function handleResponseError(error) {
	// Verificar se a requisição tem o header 'no-notification'
	const shouldShowNotification = !error.config?.headers?.["no-notification"];

	let notificationParam = {
		message: "",
	};

	if (!error.response) {
		// Sem resposta do servidor: timeout do axios (ECONNABORTED) ou queda de
		// conexão. Mensagem acionável em vez de "Erro inesperado" — a tela pode
		// continuar num spinner, mas o usuário sabe que é a conexão e pode
		// recarregar (no app: puxar pra baixo ou tocar de novo na tab).
		if (error.code === "ECONNABORTED") {
			notificationParam.message = "A conexão demorou demais";
			notificationParam.description =
				"Verifique sua internet e tente de novo.";
		} else {
			notificationParam.message = "Sem conexão com o servidor";
			notificationParam.description =
				"Verifique sua internet e tente de novo.";
		}
		if (shouldShowNotification) {
			notification.error(notificationParam);
		}
		return Promise.reject(error);
	}

	if (error.response.status === 401) {
		const isTokenRevogado =
			error.response.data?.code === CODIGO_TOKEN_REVOGADO;

		localStorage.removeItem(AUTH_TOKEN);

		if (isTokenRevogado && !redirecionandoTokenRevogado) {
			redirecionandoTokenRevogado = true;
			notification.error({
				message: "Sessão encerrada",
				description:
					"A senha da sua conta foi alterada. Faça login novamente.",
			});

			// Dentro do app profissional (WebView): o app guarda o token
			// nativo e o REINJETA no localStorage a cada load — o reload
			// abaixo reinstalaria o token revogado e viraria loop infinito
			// de "faça login novamente". Avisa o app pra deslogar
			// nativamente; e mesmo num app antigo sem o handler, ir pro
			// login SEM reload não realimenta o loop.
			if (window.ReactNativeWebView) {
				try {
					window.ReactNativeWebView.postMessage(
						JSON.stringify({
							type: "tokenRevogado",
							message: error.response.data?.message,
						}),
					);
				} catch (e) {}
				setTimeout(() => {
					window.location.hash = ENTRY_ROUTE;
				}, 300);
				return Promise.reject(error);
			}

			setTimeout(() => {
				window.location.hash = ENTRY_ROUTE;
				window.location.reload();
			}, 300);
			return Promise.reject(error);
		}

		if (!isTokenRevogado) {
			notificationParam.message = "Falha na autenticação";
			notificationParam.description =
				error.response.message ?? "E-mail ou senha inválidos";
			if (shouldShowNotification) {
				notification.error(notificationParam);
			}
		}

		return Promise.reject(error);
	}

	// Verificar se é bloqueio por falta de pagamento
	if (
		error.response.status === 403 &&
		error.response.data?.code === CODIGO_BLOQUEIO_PAGAMENTO
	) {
		// Redirecionar para a tela de conta bloqueada
		// Apenas redireciona se não estiver já na página de conta bloqueada
		if (!window.location.hash.includes("/conta-bloqueada")) {
			window.location.href = `${window.location.origin}/#${APP_PREFIX_PATH}/conta-bloqueada`;
		}
		return Promise.reject(error);
	}

	// Funil v3 (motor v2): conta pendente_ativacao/dormente que ainda não pagou
	// — manda pro checkout da oferta (a ativação do 9,90). Não redireciona se já
	// estiver lá (evita loop).
	if (
		error.response.status === 403 &&
		error.response.data?.code === CODIGO_PENDENTE_ATIVACAO
	) {
		if (!window.location.hash.includes("/checkout-oferta")) {
			window.location.href = `${window.location.origin}/#/checkout-oferta`;
		}
		return Promise.reject(error);
	}

	if (
		error.response.status === 404 ||
		error.response.status === 403 ||
		error.response.status === 400
	) {
		if (error.response.data?.message) {
			notificationParam.message = error.response.data.message;
		} else if (error.response.data?.error) {
			notificationParam.message = error.response.data.error;
		} else {
			notificationParam.message = "Not Found";
		}
	}

	if (error.response.status === 500) {
		if (error.response.data?.message) {
			notificationParam.message = error.response.data.message.toString();
		} else if (error.response.data?.error?.message) {
			notificationParam.message =
				error.response.data.error.message.toString();
		} else if (typeof error.response.data?.error == "string") {
			notificationParam.message = error.response.data.error.toString();
		} else {
			notificationParam.message = "Erro inesperado";
		}
	}

	if (error.response.status === 508) {
		notificationParam.message = "Time Out";
	}

	// Só notifica quando há O QUE dizer: status fora dos branches acima (ex.:
	// 409, tratado pelo chamador) deixava message = "" e disparava um toast
	// VAZIO ao lado da mensagem real do handler.
	if (shouldShowNotification && notificationParam.message) {
		notification.error(notificationParam);
	}

	return Promise.reject(error);
}

const ENTRY_ROUTE = "#/auth/login";
const PUBLIC_REQUEST_KEY = "public-request";
const TOKEN_PAYLOAD_KEY = "authorization";

// 30s pro tráfego normal; operações longas são promovidas pra 5 min no
// request interceptor (ver PADROES_OPERACAO_LONGA). Antes era 5 min pra tudo,
// e uma request pendurada deixava a tela em loading "infinito".
const TIMEOUT_PADRAO = 30000;
const TIMEOUT_OPERACAO_LONGA = 300000;

const config = {
	baseURL: API_BASE_URL,
	timeout: TIMEOUT_PADRAO,
};

export const apiFetchClient = axios.create(config);

apiFetchClient.interceptors.request.use(onFulfilledRequest, handleRequestError);
apiFetchClient.interceptors.response.use((response) => {
	return response.data;
}, handleResponseError);

export const apiDownloadClient = axios.create({
	...config,
	responseType: "blob",
});

apiDownloadClient.interceptors.request.use(
	onFulfilledRequest,
	handleRequestError,
);
apiDownloadClient.interceptors.response.use((response) => {
	const filename = response.headers["content-disposition"]
		.split(";")[1]
		.split("=")[1];
	FileSaver.saveAs(response.data, filename);

	return response;
}, handleResponseError);

export default apiFetchClient;
